import { z } from "zod";

/**
 * The dashboard's response contracts.
 *
 * ==========================================================================================
 * PARSED AGAINST A REAL RESPONSE, OR NOT WRITTEN
 * ==========================================================================================
 *
 * `ExpensesPageDtoSchema` drifted into describing a payload the route had not sent for three
 * commits, because nothing imported it. The shape is declared in the read model, again here, and
 * again in the SPA's props, and TypeScript connects none of the three — so a schema that is never
 * parsed is a document rather than a contract. NFR-008: `dashboard.test.ts` parses a real response
 * through this, and the assertion is part of the feature.
 *
 * ==========================================================================================
 * MONEY IS A STRING, AND THAT IS DELIBERATE
 * ==========================================================================================
 *
 * Every amount crosses as a decimal STRING of cents, not a number. `Number.MAX_SAFE_INTEGER` is not
 * the concern at household scale; JSON is. `JSON.stringify` cannot serialise a `bigint` at all, and
 * the alternative — converting to `number` at the boundary — reintroduces float exactly where the
 * whole page is sums of sums. A string survives the trip and the client parses once, where it
 * formats.
 *
 * Percentages ARE numbers, because they are already whole and small, and because they are labels
 * rather than quantities anything is computed from downstream.
 */

/** A cent amount as a decimal integer string, optionally signed. */
const CentsSchema = z.string().regex(/^-?\d+$/, "cents must be an integer string");

export const UnpricedHoldingDtoSchema = z.object({
  stockId: z.string().uuid(),
  name: z.string(),
  symbol: z.string(),
  /** Share ticks as a string, for the same reason money is. */
  shareTicks: z.string().regex(/^-?\d+$/),
});

/**
 * The three asset pillars as whole percentages.
 *
 * ABSENT, not zeroed, when the household has no assets — `.optional()` is carrying FR-018a's other
 * half. And they need not sum to 100: three whole numbers over an exhaustive whole routinely give
 * 99 or 101, and adjusting them would make a row disagree with the euro figure beside it.
 */
export const AssetSharesDtoSchema = z.object({
  savingsPercent: z.number().int(),
  investmentsPercent: z.number().int(),
  propertiesPercent: z.number().int(),
  /** Added 2026-08-22 with `vehiclesCents`. See that field for what was wrong before it. */
  vehiclesPercent: z.number().int(),
});

/**
 * The one backward comparison the card makes (AC-D19).
 *
 * Absent for a household with no month a year ago, and AC-D19a rules out any substitute — there is
 * no "since you started" delta, because a household's first recorded month is the day somebody typed
 * in their opening balances rather than a financial event.
 */
export const TrendDtoSchema = z.object({
  direction: z.enum(["up", "down", "level"]),
  deltaCents: CentsSchema,
  comparedToMonth: z.string().regex(/^\d{4}-\d{2}$/),
});

export const NetWorthDtoSchema = z.object({
  totalCents: CentsSchema,
  savingsCents: CentsSchema,
  investmentsCents: CentsSchema,
  propertiesCents: CentsSchema,
  /**
   * What the household's vehicles are worth (023).
   *
   * **ADDED 2026-08-22, AND THE CARD WAS SHORT BY IT UNTIL THEN.** The net worth SERIES has counted
   * vehicles since the pillar shipped — the chart's own total line sums this layer with the other
   * three — while `totalCents` was computed from subtotals that had no such field. A household with a
   * €12,400 car read a headline €12,400 below the line drawn directly beneath it, and the trend moved
   * by a delta the headline did not contain.
   *
   * It survived because `dashboard.test.ts`'s fixture owned no vehicle, so a layer of zeroes made the
   * two implementations indistinguishable. Giving it one made the assertion fail by exactly that.
   */
  vehiclesCents: CentsSchema,
  /** A POSITIVE magnitude. Negated at presentation, and in exactly two places — see the read model. */
  mortgagesCents: CentsSchema,
  assetShares: AssetSharesDtoSchema.optional(),
  /** Omitted when the household owns no property, or owns property nothing has valued (AC-D21). */
  loanToValuePercent: z.number().int().optional(),
  trend: TrendDtoSchema.optional(),
  /** Empty when everything is priced. Never contains a holding counted at zero (AC-D9). */
  unpricedHoldings: z.array(UnpricedHoldingDtoSchema),
});

/** A month on the chart's x-axis. `YYYY-MM`, which is what makes `axisLabel` render "Aug 2026". */
const ChartMonthSchema = z.string().regex(/^\d{4}-\d{2}$/);

export const NetWorthLayerPointSchema = z.object({
  at: ChartMonthSchema,
  /**
   * Cents, and SIGNED for the mortgage layer.
   *
   * The layer is where the sign belongs: `mortgagesCents` on the card is a positive magnitude, and
   * exactly two places negate it — that row and this point. A pre-negated value would be negated twice
   * by one of them eventually, and the symptom is a household's debt drawn as an asset.
   */
  value: CentsSchema,
});

export const NetWorthLayerDtoSchema = z.object({
  /** `savings` | `investments` | `vehicles` | `equity` — identity, never the label. */
  id: z.string(),
  label: z.string(),
  points: z.array(NetWorthLayerPointSchema),
  /**
   * The months whose real value was NEGATIVE and is drawn as zero. Only `equity` ever carries it.
   *
   * A stacked area cannot draw a negative band inside a positive stack, so a household that owes more
   * on a property than it is worth gets a floor at zero — and this is how the section says so. Absent
   * is the ordinary case; the array is never sent empty.
   */
  clampedMonths: z.array(ChartMonthSchema).optional(),
});

/**
 * The twelve-month stacked area chart (AC-D3).
 *
 * ==========================================================================================
 * THE SERVER DECIDES THE WINDOW, AND THE SERIES ARE DENSE SO THE CLIENT CANNOT MOVE IT
 * ==========================================================================================
 *
 * `months` is the window the reconstruction resolved. Every layer carries a value at every one of
 * those months, zeros included (research R9) — because `chartPositions` derives the axis from the union
 * of the points it is handed and `valueAt` carries the last value forward, so a sparse layer would let
 * the client both narrow the window and paint a month the server never computed. That is the 004 defect
 * the dense rule replaced, and it is why this is a server obligation rather than a convenience.
 *
 * There are TWELVE months here and THIRTEEN in the reconstruction: the thirteenth is M−12, which the
 * card's trend compares against and the chart does not draw (FR-031).
 */
export const NetWorthChartDtoSchema = z.object({
  months: z.array(ChartMonthSchema),
  series: z.array(NetWorthLayerDtoSchema),
});

/**
 * One bar of the surplus history.
 *
 * ==========================================================================================
 * FLAT, WHERE THE PRIMITIVE TAKES A MAP — AND THAT IS THE PAYLOAD'S IDIOM RATHER THAN THE CHART'S
 * ==========================================================================================
 *
 * `BarBucket` in `@gladis/ui` carries `values: Record<string, number>` because a stacked bar may have
 * many segments. This chart has exactly one series, so the payload sends the figure rather than a
 * one-key map: `leftCents` is a cent string like every other money field here, and the component builds
 * the primitive's shape at the point it hands it over. The contract's example shows the map; a one-key
 * record on the wire would have to be unwrapped anyway, and mixing a number-valued map into a payload
 * whose every other amount is a string is the part that would actually confuse.
 *
 * There is deliberately NO `projected` key of any kind (FR-063) — not empty, not null, absent. The
 * primitive supports a faded band above the solid part and income's chart uses one; forecasting the rest
 * of this month is a stated non-goal, so the field never appears.
 */
export const SurplusBucketDtoSchema = z.object({
  key: ChartMonthSchema,
  /** SIGNED. A deficit draws below the axis at full weight; clamping it would hide the bar (AC-D12). */
  leftCents: CentsSchema,
  /** True for the current month only. Says the month is still running, and nothing more. */
  partial: z.boolean(),
});

/**
 * Twelve bars of household `Left` (AC-D22).
 *
 * **Rides with the PAGE read, not the health read** (FR-005), and that is a statement about what can
 * invalidate it: the chart is fixed at household scope and a rolling twelve months, so no control on the
 * health card beside it can change a bar. Grouping it with that card by topic would have coupled it to a
 * filter it does not obey.
 */
export const SurplusChartDtoSchema = z.object({
  buckets: z.array(SurplusBucketDtoSchema),
});

/**
 * One row of the expense ranking.
 *
 * The `category` alone, with no `label`. Every picker, chip and row in the application resolves the
 * name from the value, so a label on the wire would be a second source for the same string, and the
 * one that goes stale is the copy — the surplus chart's bucket labels are derived client-side for
 * the same reason.
 *
 * `expenseCategoryLabel` MOVED TO `@gladis/ui/i18n` in feature 021, and the reason is this comment's
 * own argument taken one step further: a label is a sentence, and a sentence in the shared kernel is
 * a sentence `apps/api` imports and no reader ever sees. The values stay here; the words are at the
 * edge, in two languages.
 */
export const ExpenseCategoryTotalDtoSchema = z.object({
  category: z.string(),
  monthlyCents: CentsSchema,
});

/**
 * ⑤ The Expense Summary Card (AC-D5).
 *
 * ==========================================================================================
 * THE ROWS DO NOT SUM TO THE TOTAL, AND NO PROPORTIONAL MARK MAY ACCOMPANY THEM
 * ==========================================================================================
 *
 * `categories` is the top ten of up to seventeen, with no "Other" bucket and no remainder row — so a
 * household using fourteen sees ten and the four smallest are absent. FR-067 forbids a percentage or any
 * part-of-whole mark beside a row precisely because of that: a share of a whole the rows do not add up
 * to is a lie the reader has no way to detect.
 *
 * `totalCents` is the whole of it, including the categories that are not shown AND this month's derived
 * mortgage interest — which is the same figure the health card reports as `Out` for this month (AC-D11),
 * because both are grouped from one reconstruction.
 */
export const ExpenseSummaryDtoSchema = z.object({
  totalCents: CentsSchema,
  categories: z.array(ExpenseCategoryTotalDtoSchema),
});

/**
 * ⑥ One line of the activity list.
 *
 * `message` is the FINISHED SENTENCE, composed by whichever context emitted the event and stored with
 * it — the reader assembles nothing. `actorDisplayName` is the name that member had when it happened,
 * snapshotted at write time and never a live lookup (AC-AL3): a member who has since been renamed, or
 * removed, still reads correctly against the event they caused.
 *
 * There is no `entityType`/`entityId` pair here, unlike the Activity page's own rows. The dashboard's
 * five lines are context rather than navigation — the card's one link goes to the feed, which is where a
 * row leads somewhere.
 */
export const RecentActivityDtoSchema = z.object({
  id: z.string(),
  occurredAt: z.iso.datetime(),
  actorDisplayName: z.string(),
  message: z.string(),
});

/** The six modules ⑦ reports, in the order it renders them. Mortgages have NO row (FR-076, AC-D8). */
export const FreshnessModuleSchema = z.enum([
  "savings",
  "investments",
  "properties",
  /*
   * 023. Its row is load-bearing in a way the others are not: a vehicle's value is observed and
   * carried forward with nothing modelled between observations, so one nobody refreshes produces a
   * net worth band that is flat and increasingly wrong. For savings a balance at least changes on a
   * rhythm the household can feel.
   */
  "vehicles",
  "expenses",
  "income",
]);

/**
 * ⑦ One freshness row.
 *
 * `lastUpdatedAt` is an ISO-8601 INSTANT and never a bare date, because three of the five sources are
 * date columns and two are timestamps — one field, one type, with a date widened to midnight UTC by the
 * read. `null` means "No data yet" and the row keeps its link (FR-078); it is never omitted, because an
 * absent row is indistinguishable from a module that does not exist.
 */
export const FreshnessRowDtoSchema = z.object({
  module: FreshnessModuleSchema,
  lastUpdatedAt: z.iso.datetime().nullable(),
});

export const DashboardDtoSchema = z.object({
  netWorth: NetWorthDtoSchema,
  netWorthChart: NetWorthChartDtoSchema,
  surplusChart: SurplusChartDtoSchema,
  /** Up to five, newest first. `[]` is a valid state and the card renders an empty state (FR-073). */
  recentActivity: z.array(RecentActivityDtoSchema),
  /** ALWAYS six since 023, always in `FreshnessModuleSchema`'s order. */
  freshness: z.array(FreshnessRowDtoSchema),
});

/** The two windows the health card offers. `this-year` is the calendar year to date. */
export const HealthPeriodSchema = z.enum(["this-month", "this-year"]);

/**
 * Which of the four things the card has to say (AC-D16, FR-053 – FR-055).
 *
 * It is about the RECORDS, not about this period's figures. A household whose only income source
 * ended in 2023 has income recorded and a real deficit this month; one that has never entered any is
 * being asked a question about an empty table, and a deficit equal to its whole expense total would
 * be a statement about its finances rather than about its data.
 */
export const HealthEmptinessSchema = z.enum(["none", "no-income", "no-expenses", "neither"]);

export const DashboardHealthDtoSchema = z.object({
  /*
   * WHERE THE MONEY GOES, and it is on THIS payload rather than the page's since 2026-08-14.
   *
   * It follows the scope filter — AC-D11 requires its headline to be this card's `Out` for the current
   * month, and a household-only ranking beside a per-member `Out` made that criterion false as soon as
   * anyone picked a member. It does NOT follow the period: the section is "per month" by name, and a
   * year-scoped ranking would be a different card.
   */
  expenseSummary: ExpenseSummaryDtoSchema,
  /**
   * ECHOED, both of them, and that is a correctness requirement rather than a convenience.
   *
   * Both filters live on the card and each change is its own request. Without the echo a slow
   * response for the previous scope repaints the figures of a filter the member has already left —
   * and the figures are plausible, because they are somebody's.
   */
  scope: z.string(),
  period: HealthPeriodSchema,

  inCents: CentsSchema,
  /** A POSITIVE magnitude, negated at presentation — the same rule the mortgage subtotal follows. */
  outCents: CentsSchema,
  /** SIGNED, never clamped. A deficit is the most important thing this card can say (AC-D12). */
  leftCents: CentsSchema,
  /** `left / in`. ABSENT when `inCents` is 0 — a percentage of nothing is not 0% (FR-041). */
  keptPercent: z.number().int().optional(),
  /**
   * Joint income in the period, which is NEVER divided between members (AC-D15).
   *
   * `null` at household scope, where it is already inside `inCents`. Null rather than absent, because
   * at household scope the answer is "not applicable" rather than "unknown", and the card renders the
   * row only for a member.
   */
  jointIncomeCents: CentsSchema.nullable(),
  /**
   * Whether the per-member figures can be expected to sum to the household's.
   *
   * A property of the household's DATA, so it is computed identically at both scopes: the card states
   * the mismatch rather than presenting figures that appear to add up and do not (FR-052).
   */
  reconciles: z.boolean(),
  emptiness: HealthEmptinessSchema,
});

export type UnpricedHoldingDto = z.infer<typeof UnpricedHoldingDtoSchema>;
export type AssetSharesDto = z.infer<typeof AssetSharesDtoSchema>;
export type TrendDto = z.infer<typeof TrendDtoSchema>;
export type NetWorthDto = z.infer<typeof NetWorthDtoSchema>;
export type NetWorthLayerDto = z.infer<typeof NetWorthLayerDtoSchema>;
export type NetWorthChartDto = z.infer<typeof NetWorthChartDtoSchema>;
export type SurplusBucketDto = z.infer<typeof SurplusBucketDtoSchema>;
export type SurplusChartDto = z.infer<typeof SurplusChartDtoSchema>;
export type ExpenseCategoryTotalDto = z.infer<typeof ExpenseCategoryTotalDtoSchema>;
export type ExpenseSummaryDto = z.infer<typeof ExpenseSummaryDtoSchema>;
export type RecentActivityDto = z.infer<typeof RecentActivityDtoSchema>;
export type FreshnessModule = z.infer<typeof FreshnessModuleSchema>;
export type FreshnessRowDto = z.infer<typeof FreshnessRowDtoSchema>;
export type DashboardDto = z.infer<typeof DashboardDtoSchema>;
export type HealthPeriod = z.infer<typeof HealthPeriodSchema>;
export type HealthEmptiness = z.infer<typeof HealthEmptinessSchema>;
export type DashboardHealthDto = z.infer<typeof DashboardHealthDtoSchema>;
